import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Card, Chip, Paper, Typography, IconButton, Tooltip} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import properties from "constants/ApplicationConstants";
import {formatDesignation} from "constants/ApplicationConstants";
import {GET} from "APIclient";
import dayjs from "dayjs";

const formatDate = date => (date ? dayjs(date).format("DD MMM YYYY") : "N/A");
const PAGE_SIZE = 20;

const WorkAnniversaryList = () => {
  const navigate = useNavigate();
  const [workAnniversaryData, setWorkAnniversaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    const fetchCompletedAnniversaries = async () => {
      setLoading(true);
      try {
        const response = await GET(
          `admin/get-employee-work-anniversary?milestoneYears=0&page=${page}&size=${PAGE_SIZE}`
        );

        if (response?.status === 200) {
          const data = response?.data?.data;
          const formatted = (data?.paginatedData || []).map(item => ({
            ...item,
            designation: formatDesignation(item?.designation)
          }));
          setWorkAnniversaryData(formatted);
          setTotalElements(data?.totalItems ?? data?.totalElements ?? 0);
        } else {
          setWorkAnniversaryData([]);
          setTotalElements(0);
        }
      } catch (error) {
        console.error("Error fetching completed work anniversary data:", error);
        setWorkAnniversaryData([]);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedAnniversaries();
  }, [page]);

  const rows = useMemo(
    () =>
      workAnniversaryData.map((item, index) => ({
        ...item,
        id: item.empId || `${page}-${index}`,
        srNo: page * PAGE_SIZE + index + 1
      })),
    [workAnniversaryData, page]
  );

  const columns = [
    {
      field: "srNo",
      headerName: "#",
      width: 50,
      flex: 0.3,
      sortable: false,
      filterable: false
    },
    {
      field: "empId",
      headerName: "EMP ID",
      flex: 0.7,
      minWidth: 90
    },
    {
      field: "empName",
      headerName: "Employee Name",
      flex: 1.2,
      minWidth: 130
    },
    {
      field: "designation",
      headerName: "Designation",
      flex: 1.1,
      minWidth: 120
    },
    {
      field: "dateOfJoining",
      headerName: "Joining Date",
      flex: 1,
      minWidth: 120,
      renderCell: params => formatDate(params.value)
    },
    {
      field: "completedYearsOfService",
      headerName: "Completed Service",
      flex: 1.2,
      minWidth: 140
    },
    {
      field: "fiveYearAnniversaryDate",
      headerName: "5 Year Anniversary",
      flex: 1.1,
      minWidth: 130,
      renderCell: params => formatDate(params.value)
    },
    {
      field: "tenYearAnniversaryDate",
      headerName: "10 Year Anniversary",
      flex: 1.1,
      minWidth: 130,
      renderCell: params => formatDate(params.value)
    }
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={2}>
        <Card>
          <MDBox p={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Back to Dashboard">
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>

                <Typography variant="h6" fontWeight="bold" sx={{fontFamily: properties.fontFamily}}>
                  Work Anniversary List
                </Typography>
              </Box>

              <Chip
                label={`Total Employees: ${totalElements}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </MDBox>
        </Card>

        <MDBox
          mt={2}
          sx={{
            overflow: "hidden"
          }}
        >
          <Paper
            sx={{
              width: "100%",
              overflow: "hidden"
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={row => row.empId}
              loading={loading}
              autoHeight={false}
              paginationMode="server"
              rowCount={totalElements}
              paginationModel={{page, pageSize: PAGE_SIZE}}
              onPaginationModelChange={model => {
                setPage(model.page);
              }}
              pageSizeOptions={[20]}
              disableRowSelectionOnClick
              sx={{
                height: "calc(100vh - 250px)",
                minHeight: 470,
                width: "100%",
                fontFamily: properties.fontFamily,
                fontSize: "14px",

                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F5F7FA"
                },

                "& .MuiDataGrid-virtualScroller": {
                  overflowY: "auto",
                  overflowX: "hidden"
                }
              }}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
};

export default WorkAnniversaryList;
